import {
  PROJECT_COMPLEXITY,
  ROUTINE_FREQUENCIES,
  type ProjectComplexity,
  type RoutineFrequency,
  type EcologicalAreaName
} from '../constants/ecologicalAreas';
import type { FinanceXPEvent } from '../types/ecological';
import { POINTS } from './levelCalculation';

export interface XPDistribution {
  area: EcologicalAreaName;
  xp: number;
}

export function calculateProjectXP(
  complexity: ProjectComplexity,
  linkedAreas: EcologicalAreaName[]
): XPDistribution[] {
  const baseXP = PROJECT_COMPLEXITY[complexity].baseXP;
  const areaCount = linkedAreas.length || 1;
  const xpPerArea = Math.floor(baseXP / areaCount);

  return linkedAreas.map(area => ({
    area,
    xp: xpPerArea
  }));
}

export function calculateTotalProjectXP(complexity: ProjectComplexity): number {
  return PROJECT_COMPLEXITY[complexity].baseXP;
}

export function calculateRoutineXP(
  frequency: RoutineFrequency,
  linkedAreas: EcologicalAreaName[],
  customXP?: number
): XPDistribution[] {
  const baseXP = customXP || ROUTINE_FREQUENCIES[frequency].baseXP;
  const areaCount = linkedAreas.length || 1;
  const xpPerArea = Math.floor(baseXP / areaCount);

  return linkedAreas.map(area => ({
    area,
    xp: xpPerArea
  }));
}

export function calculateRoutineBaseXP(frequency: RoutineFrequency): number {
  return ROUTINE_FREQUENCIES[frequency].baseXP;
}

// Tier ladders for the two amount-dependent finance rewards. Mutable
// hydration targets (xp_rates kinds 'savings_milestone' / 'goal_size' —
// threshold = the percent/size floor, multiplier holds the XP). Kept sorted
// by threshold ascending; lookup takes the highest floor at/below the value.
export const SAVINGS_MILESTONE_LADDER: Array<{ threshold: number; xp: number }> = [
  { threshold: 0, xp: 25 },
  { threshold: 50, xp: 50 },
  { threshold: 75, xp: 75 },
  { threshold: 100, xp: 100 },
];
export const GOAL_SIZE_LADDER: Array<{ threshold: number; xp: number }> = [
  { threshold: 0, xp: 100 },
  { threshold: 1000, xp: 200 },
  { threshold: 5000, xp: 300 },
  { threshold: 10000, xp: 500 },
];

function ladderXP(ladder: Array<{ threshold: number; xp: number }>, value: number): number {
  let xp = ladder[0]?.xp ?? 0;
  for (const step of ladder) {
    if (value >= step.threshold) xp = step.xp;
    else break;
  }
  return xp;
}

export function hydrateEcologicalXpLadders(
  rates: Array<{ kind: string; threshold: number | null; multiplier: number }>,
): void {
  const build = (kind: string) =>
    rates
      .filter((r) => r.kind === kind && Number.isFinite(r.multiplier))
      .map((r) => ({ threshold: Number(r.threshold ?? 0), xp: r.multiplier }))
      .sort((a, b) => a.threshold - b.threshold);
  const savings = build('savings_milestone');
  if (savings.length > 0) {
    SAVINGS_MILESTONE_LADDER.length = 0;
    SAVINGS_MILESTONE_LADDER.push(...savings);
  }
  const goals = build('goal_size');
  if (goals.length > 0) {
    GOAL_SIZE_LADDER.length = 0;
    GOAL_SIZE_LADDER.push(...goals);
  }
}

// Config-backed via POINTS (console → XP economy); the literals in POINTS
// are the defaults, identical to the original values here.
const FINANCE_XP_VALUES: Record<FinanceXPEvent['type'], number | ((amount?: number) => number)> = {
  get transaction_logged() { return POINTS.ECO_TRANSACTION ?? 5; },
  get transaction_categorized() { return POINTS.ECO_TRANSACTION_CATEGORIZED ?? 2; },
  get budget_created() { return POINTS.ECO_BUDGET_CREATED ?? 20; },
  get budget_met() { return POINTS.ECO_BUDGET_MET ?? 50; },
  savings_milestone: (percent?: number) => ladderXP(SAVINGS_MILESTONE_LADDER, percent ?? 0),
  goal_completed: (goalSize?: number) => ladderXP(GOAL_SIZE_LADDER, goalSize ?? 0),
};

export function calculateFinanceXP(event: FinanceXPEvent): number {
  const xpValue = FINANCE_XP_VALUES[event.type];

  if (typeof xpValue === 'function') {
    return xpValue(event.amount);
  }

  return xpValue;
}

export function getFinanceXPDescription(event: FinanceXPEvent): string {
  switch (event.type) {
    case 'transaction_logged':
      return 'Logged a transaction';
    case 'transaction_categorized':
      return 'Categorized a transaction';
    case 'budget_created':
      return 'Created a new budget';
    case 'budget_met':
      return 'Stayed within monthly budget';
    case 'savings_milestone':
      return `Reached ${event.amount}% of savings goal`;
    case 'goal_completed':
      return event.description || 'Completed a financial goal';
    default:
      return 'Financial activity';
  }
}

export function calculateLevelFromXP(totalXP: number): {
  level: number;
  currentLevelXP: number;
  xpToNextLevel: number;
} {
  let level = 1;
  let xpRemaining = totalXP;

  while (level < 100) {
    const xpForLevel = 100 * level;
    if (xpRemaining < xpForLevel) {
      break;
    }
    xpRemaining -= xpForLevel;
    level++;
  }

  return {
    level,
    currentLevelXP: xpRemaining,
    xpToNextLevel: 100 * level
  };
}

export function calculateXPForLevel(level: number): number {
  let totalXP = 0;
  for (let i = 1; i < level; i++) {
    totalXP += 100 * i;
  }
  return totalXP;
}

export function getProgressPercentage(currentLevelXP: number, xpToNextLevel: number): number {
  if (xpToNextLevel === 0) return 100;
  return Math.min(100, Math.round((currentLevelXP / xpToNextLevel) * 100));
}

export function calculateEcologicalDomainLevel(areaLevels: number[]): number {
  if (areaLevels.length === 0) return 1;
  const sum = areaLevels.reduce((acc, level) => acc + level, 0);
  return Math.floor(sum / areaLevels.length);
}

export function calculateEcologicalDomainXP(areaTotalXPs: number[]): number {
  if (areaTotalXPs.length === 0) return 0;
  return areaTotalXPs.reduce((acc, xp) => acc + xp, 0);
}

export function isRoutineDue(
  frequency: RoutineFrequency,
  lastCompletedAt: string | null
): boolean {
  if (!lastCompletedAt) return true;

  const lastCompleted = new Date(lastCompletedAt);
  const now = new Date();
  const daysSinceCompletion = Math.floor(
    (now.getTime() - lastCompleted.getTime()) / (1000 * 60 * 60 * 24)
  );

  const intervalDays = ROUTINE_FREQUENCIES[frequency].daysInterval;
  return daysSinceCompletion >= intervalDays;
}

export function getDaysUntilDue(
  frequency: RoutineFrequency,
  lastCompletedAt: string | null
): number {
  if (!lastCompletedAt) return 0;

  const lastCompleted = new Date(lastCompletedAt);
  const now = new Date();
  const daysSinceCompletion = Math.floor(
    (now.getTime() - lastCompleted.getTime()) / (1000 * 60 * 60 * 24)
  );

  const intervalDays = ROUTINE_FREQUENCIES[frequency].daysInterval;
  return Math.max(0, intervalDays - daysSinceCompletion);
}
